import type {StoreShapeType} from 'app/reducers/types';
import type {
  BasicTeamSerializedType,
  FolderRecurrentParentSerializedType,
  ListResponseType,
  RawFolderRecurrentParentSerializedType,
  RawFolderSerializedType,
} from 'sweet-types';

export function selectFolderWithAncestors(
  hashid: string | void,
  state: StoreShapeType
): null | FolderRecurrentParentSerializedType {
  const rawFolder =
    hashid != null ? state.foldersArea.getIn(['byId', hashid]) : null;

  if (rawFolder == null) {
    return null;
  }

  return populateFolderParentRecursively(
    rawFolder.toJS(),
    state.foldersArea.get('byId'),
    (folder) => populateFolderTeams(folder, state.teamsArea.get('basicTeams'))
  );
}

export function rehydrateFoldersTeams<
  T extends {teams: Array<{type: 'basic_team'; id: number}>}
>(
  response: ListResponseType<
    T,
    {basic_team: Record<number, BasicTeamSerializedType>}
  >,
  basic_teams?: Record<number, BasicTeamSerializedType>
): ListResponseType<T & {teams: Array<BasicTeamSerializedType>}> {
  const results = response.results.map((folder) =>
    rehydrateFolderTeams(folder, basic_teams ?? {})
  );
  return {...response, results, related: undefined};
}

export function rehydrateFolderTeams<
  T extends {teams: Array<{type: 'basic_team'; id: number}>}
>(folder: T, basic_teams: Record<number, BasicTeamSerializedType>) {
  const teams: Array<BasicTeamSerializedType> = folder.teams.map(
    (team) =>
      basic_teams[team.id] || {
        id: team.id,
        url: '',
        name: 'Untitled Team',
        content_type: 'team',
        hashid: '',
        html_url: '',
      }
  );
  return {...folder, teams};
}

export function populateFolderTeams(
  folder: RawFolderRecurrentParentSerializedType,
  basicTeams: any
): FolderRecurrentParentSerializedType {
  const {teams, ...fields} = folder;
  return {
    ...fields,
    teams: teams.map<BasicTeamSerializedType>(({id}) =>
      basicTeams.get(id.toString()).toJS()
    ),
  };
}

function populateFolderParentRecursively(
  folder: RawFolderSerializedType,
  byId: any,
  postProcessor: (
    arg0: RawFolderRecurrentParentSerializedType
  ) => FolderRecurrentParentSerializedType
): null | FolderRecurrentParentSerializedType {
  const {parent, ...fields} = folder;
  let parentFolder: null | FolderRecurrentParentSerializedType = null;

  if (parent != null) {
    const immutableParent = byId.get(parent);

    // if the folder has a parent but we can't get parent data, return null
    if (immutableParent == null) {
      return null;
    }

    parentFolder = populateFolderParentRecursively(
      immutableParent.toJS(),
      byId,
      postProcessor
    );
  }

  return postProcessor({...fields, parent: parentFolder});
}
